import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getByID, subscribeAllEntities } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { Student } from '@chancenwerk/master-data-management/users/entities/Student.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { combineLatest, switchMap } from 'rxjs';
import { PollClick, PollLink } from '../entities/PollLink.js';

function get(object: any, path: string | string[], defaultValue = undefined) {
  if (typeof path === 'string') {
    path = path.split(/[.\[\]\"]+/).filter(x => x);
  }

  if (path.length === 0) {
    return object;
  }

  const [head, ...tail] = path;
  if (!head) {
    return object;
  }

  if (!(head in object)) {
    return defaultValue;
  }

  return get(object[head], tail, defaultValue);
}

export namespace PollAPI {
  export const generateURL = registerQuery({
    type: () => String,
    operationName: 'PollAPI_generateURL',
    resolve: (pollId: string) => {
      return async () => {
        const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({ _id: 1 });
        if (!user) {
          throw new Error('User not found');
        }

        const poll = await getByID(
          PollLink,
          pollId,
        )({
          url: 1,
          mapping: {
            pollKey: 1,
            entityField: 1,
          },
        });
        if (!poll) {
          throw new Error('Poll not found');
        }

        const userCol = await getRawCollection<ChancenwerkUser>('adornisuser-materialized');
        const userFull = await userCol.findOne({ _id: user._id });
        if (!userFull) {
          throw new Error('User not found');
        }

        const kv: Record<string, string> = {};
        for (const field of poll.mapping ?? []) {
          if (!field.pollKey || !field.entityField) {
            continue;
          }

          const val = get(userFull, field.entityField);
          if (val) {
            switch (typeof val) {
              case 'string':
                kv[field.pollKey] = val;
                break;
              case 'boolean':
              case 'number':
                kv[field.pollKey] = val.toString();
                break;
              default:
                throw new Error(`Unsupported type: ${typeof val}`);
            }
          }
        }

        const baseUrl = poll.url?.endsWith('/') ? poll.url : `${poll.url}/`;
        const queryString = Object.entries(kv)
          .map(([key, value]) => `${encodeURIComponent(value)}=${1}`)
          .join('&');
        return `${baseUrl}?${queryString}`;
      };
    },
    params: [{ type: () => String, name: 'pollId' }],
  });

  export const registerClick = registerMutation({
    type: () => Boolean,
    operationName: 'PollAPI_registerClick',
    resolve: (pollId: string) => {
      return async () => {
        const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({ _id: 1 });
        if (!user) {
          throw new Error('User not found');
        }

        new PollClick({
          pollId,
          userId: user._id,
        }).create();

        return true;
      };
    },
    params: [{ type: () => String, name: 'pollId' }],
  });

  export const subscribeRelevantPolls = registerSubscription({
    type: () => [PollLink],
    operationName: 'PollAPI_subscribeRelevantPolls',
    params: [{ type: () => Boolean, name: 'hideClicked' }],
    resolve: (hideClicked: boolean = false) => {
      return (gqlFields: BaseQLSelectionSet<PollLink>) => {
        return combineLatest([
          subscribeAllEntities(PollLink)({
            _id: 1,
            public: 1,
            url: 1,
            name: 1,
            showClass: 1,
            products: 1,
            roles: 1,
            studentType: 1,
            mapping: {
              pollKey: 1,
              entityField: 1,
            },
          }),
          watchQuery(PollClick._collectionName, {}),
        ]).pipe(
          switchMap(async ([polls, pollClicks]) => {
            const user = await CurrentUserInfo.getMyself<ChancenwerkUser>()({ _id: 1 });
            if (!user) {
              return [];
            }

            const userCol = await getRawCollection<Student | Mitarbeiter>('adornisuser-materialized');
            const userFull = await userCol.findOne({ _id: user._id });
            if (!userFull) {
              return [];
            }

            const filter: string[] = [];
            if (hideClicked) {
              const pollClickCol = await getRawCollection<PollClick>(PollClick._collectionName);
              const clickedPolls = await pollClickCol.find({ userId: user._id }).toArray();
              filter.push(...((clickedPolls?.map(p => p.pollId) ?? []) as string[]));
            }

            return polls.filter(p => {
              if (filter.length && filter.includes(p._id)) {
                return false;
              }

              if (!p.public || p.showClass !== userFull?._class) {
                return false;
              }

              if (p.showClass === Student._class) {
                const student = userFull as Student;

                if (p.products) {
                  if (!student.kpfs?.some(kpf => p.products?.includes(kpf.produkt?._class ?? ''))) {
                    return false;
                  }
                }

                if (p.studentType) {
                  if (student.schuelertyp !== p.studentType) {
                    return false;
                  }
                }
              }

              if (p.showClass === Mitarbeiter._class || p.showClass === VerwaltungsMitarbeiter._class) {
                const mitarbeiter = userFull as Mitarbeiter | VerwaltungsMitarbeiter;

                if (p.roles) {
                  if (!p.roles.every(r => mitarbeiter.roles.some(r2 => r2.name === r))) {
                    return false;
                  }
                }
              }

              return true;
            });
          }),
        );
      };
    },
  });
}
